import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import axios from "axios";
import {
  Alert,
  Box,
  Card,
  CircularProgress,
  Chip,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import AttachMoneyOutlinedIcon from "@mui/icons-material/AttachMoneyOutlined";
import SearchBar from "../../components/common/SearchBar/SearchBar";
import { API_ENDPOINT } from "../../services/api/resources";

const currency = (value, code) =>
  new Intl.NumberFormat(code === "INR" ? "en-IN" : "en-US", {
    style: "currency",
    currency: code,
    maximumFractionDigits: 2,
  }).format(Number(value || 0));

const AIUsageCostAccuracy = () => {
  const adminData = useSelector((state) => state.loginData.value);
  const admin_token = adminData?.token;
  const auth_token = adminData?.auth_token;
  const [rows, setRows] = useState([]);
  const [grandTotal, setGrandTotal] = useState({});
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchUsage = async () => {
      setLoading(true);
      setError("");
      try {
        const res = await axios.post(
          API_ENDPOINT.superadmin_ai_usage_cost_accuracy,
          { admin_token },
          { headers: { Authorization: `Bearer ${auth_token}` } },
        );
        setRows(Array.isArray(res?.data?.results) ? res.data.results : []);
        setGrandTotal(res?.data?.grand_total || {});
      } catch {
        setError("Failed to fetch API usage cost.");
      } finally {
        setLoading(false);
      }
    };

    if (admin_token) fetchUsage();
  }, [admin_token, auth_token]);

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (buyer) =>
        String(buyer.company_name || "").toLowerCase().includes(q) ||
        String(buyer.buyer_admin_token || "").toLowerCase().includes(q) ||
        (buyer.processes || []).some((item) =>
          String(item.processor_name || "").toLowerCase().includes(q),
        ),
    );
  }, [rows, search]);

  const processCount = rows.reduce(
    (sum, buyer) => sum + (buyer.processes?.length || 0),
    0,
  );

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, minHeight: "100vh", backgroundColor: "#f9fafb" }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, flexWrap: "wrap", mb: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ color: "#2a68be", fontWeight: "bold" }}>
            Buyer API Usage Cost
          </Typography>
          <Typography sx={{ fontSize: 13, color: "#6b7280", mt: 0.5 }}>
            Monitor AI accuracy usage cost by buyer and processor.
          </Typography>
        </Box>
        <Box sx={{ width: { xs: "100%", md: 420 } }}>
          <SearchBar
            value={search}
            onChange={setSearch}
            placeholder="Search company, token, or processor"
          />
        </Box>
      </Box>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={4}>
          <SummaryCard label="Grand Total INR" value={currency(grandTotal.accuracy_cost_inr, "INR")} />
        </Grid>
        <Grid item xs={12} md={4}>
          <SummaryCard label="Grand Total USD" value={currency(grandTotal.accuracy_cost_usd, "USD")} />
        </Grid>
        <Grid item xs={12} md={4}>
          <SummaryCard label="Tracked Processors" value={processCount} />
        </Grid>
      </Grid>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: 2 }}>
        <Table sx={{ minWidth: 980 }}>
          <TableHead>
            <TableRow sx={{ backgroundColor: "rgb(224, 233, 238)" }}>
              <TableCell sx={thStyle}>Company</TableCell>
           
              <TableCell sx={thStyle}>Processor</TableCell>
              <TableCell sx={thStyle} align="right">Cost USD</TableCell>
              <TableCell sx={thStyle} align="right">Cost INR</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                  <CircularProgress size={32} />
                  <Typography sx={{ mt: 1, fontSize: 13, color: "#6b7280" }}>
                    Loading API usage...
                  </Typography>
                </TableCell>
              </TableRow>
            ) : filteredRows.length ? (
              filteredRows.map((buyer) => (
                <React.Fragment key={buyer.buyer_admin_token || buyer.company_name}>
                  {(buyer.processes || []).map((process, index) => (
                    <TableRow hover key={`${buyer.buyer_admin_token}-${process.processor_name}`}>
                      {index === 0 && (
                        <>
                          <TableCell sx={tdStyle} rowSpan={(buyer.processes || []).length + 1}>
                            <Typography sx={{ fontFamily: "GilroyBold", fontSize: 14 }}>
                              {buyer.company_name || "NA"}
                            </Typography>
                          </TableCell>
                       
                        </>
                      )}
                      <TableCell sx={tdStyle}>{process.processor_name || "NA"}</TableCell>
                      <TableCell sx={tdStyle} align="right">{currency(process.accuracy_cost_usd, "USD")}</TableCell>
                      <TableCell sx={tdStyle} align="right">{currency(process.accuracy_cost_inr, "INR")}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow sx={{ backgroundColor: "#f8fafc" }}>
                    <TableCell sx={{ ...tdStyle, fontFamily: "GilroyBold" }}>Buyer Total</TableCell>
                    <TableCell sx={{ ...tdStyle, fontFamily: "GilroyBold" }} align="right">{currency(buyer.total?.accuracy_cost_usd, "USD")}</TableCell>
                    <TableCell sx={{ ...tdStyle, fontFamily: "GilroyBold" }} align="right">{currency(buyer.total?.accuracy_cost_inr, "INR")}</TableCell>
                  </TableRow>
                </React.Fragment>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                  No API usage found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

const SummaryCard = ({ label, value }) => (
  <Card sx={{ p: 2, borderRadius: 2, boxShadow: "0 8px 20px rgba(15, 23, 42, 0.08)" }}>
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
      <Box sx={{ width: 42, height: 42, borderRadius: 2, display: "grid", placeItems: "center", background: "#eaf3ff", color: "#0b63a9" }}>
        <AttachMoneyOutlinedIcon />
      </Box>
      <Box>
        <Typography sx={{ fontSize: 13, color: "#64748b", fontFamily: "gilroyMedium" }}>{label}</Typography>
        <Typography sx={{ fontSize: 22, color: "#111827", fontFamily: "GilroyBold" }}>{value}</Typography>
      </Box>
    </Box>
  </Card>
);

const thStyle = {
  fontSize: "14px",
  fontWeight: 600,
  border: "none",
  whiteSpace: "nowrap",
  fontFamily: "GilroyMedium",
};

const tdStyle = {
  fontSize: "14px",
  border: "none",
  color: "#1f2937",
  fontFamily: "GilroyMedium",
};

export default AIUsageCostAccuracy;
